import React from "react";
import {Button, Stack, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {
    OrganizationDKVCustomerNumberCreateRequest,
    useOrganizationDKVCustomerNumberCreateMutation,
} from "../../app/api/api";
import * as yup from "yup";
import {useFormik} from "formik";
import {useSnackbar} from "notistack";
import DefaultDialog, {DefaultDialogSize} from "../../components/Dialog/ClosableDialog";

interface DKVCustomerNumberCreateDialogProps {
    open: boolean;
    onClose: () => void;
    organizationId: number;
}

export default function DKVCustomerNumberCreateDialog({
    open,
    onClose,
    organizationId,
}: DKVCustomerNumberCreateDialogProps) {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [createDKVCustomerNumber] = useOrganizationDKVCustomerNumberCreateMutation();

    const handleClose = () => {
        onClose?.();
        formik.resetForm();
    };

    const validationSchema = yup.object({
        dkvCustomerNumber: yup.string().required(t("validation_inputRequired")),
        name: yup.string().required(t("validation_inputRequired")),
    });

    const formik = useFormik({
        initialValues: {
            dkvCustomerNumber: "",
            name: "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const requestData = {
                organizationId: organizationId,
                ...values,
            } as OrganizationDKVCustomerNumberCreateRequest;
            try {
                await createDKVCustomerNumber(requestData).unwrap();
                enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});
                handleClose();
            } catch {
                handleClose();
            }
        },
    });

    return (
        <DefaultDialog
            size={DefaultDialogSize.MEDIUM}
            open={open}
            titleKey={"action_createDKVCustomerNumber"}
            onClose={handleClose}
            actions={
                <Button onClick={formik.submitForm} color="primary">
                    {t("action_apply")}
                </Button>
            }
        >
            <form onSubmit={formik.handleSubmit}>
                <Stack
                    spacing={3}
                    sx={{
                        mt: 3,
                    }}
                >
                    <TextField
                        fullWidth
                        id="dkvCustomerNumber"
                        name="dkvCustomerNumber"
                        label={t("organizations_dkvCustomerNumber")}
                        value={formik.values.dkvCustomerNumber}
                        onChange={formik.handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                    />
                    <TextField
                        fullWidth
                        id="name"
                        name="name"
                        label={t("generic_name")}
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                    />
                </Stack>
            </form>
        </DefaultDialog>
    );
}
